import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import React, { useEffect, useState } from "react"
import { MdCancel, MdEdit, MdSave } from "react-icons/md"
import {
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import Swal from "sweetalert2"
import { fetchData, FetchPutData } from "utils/Common_api_functions"

const RangesIndex = () => {
  const [loading, setLoading] = useState(false)
  const [data, setdata] = useState([])
  const [selectedItem, setselectedItem] = useState({})
  const [editModal, setEditModal] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    fetchRanges()
  }, [])

  async function fetchRanges() {
    const APIdata = await fetchData(setLoading, "gauge/ranges")
    if (APIdata?.success) {
      setdata(APIdata?.data)
    } else {
      Swal.fire({
        title: "Error Fetching Ranges",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  // ── Validation: must be in strict ascending order: start < min < mid < max ──
  const validate = item => {
    const errs = {}
    const start = Number(item?.start)
    const min = Number(item?.min)
    const mid = Number(item?.mid)
    const max = Number(item?.max)

    if (isNaN(start)) errs.start = "Start value must be a number."
    if (isNaN(min)) errs.min = "Mid Value 1 must be a number."
    if (isNaN(mid)) errs.mid = "Mid Value 2 must be a number."
    if (isNaN(max)) errs.max = "Maximum value must be a number."

    if (!Object.keys(errs).length) {
      if (min <= start)
        errs.min = `Mid Value 1 must be greater than Start Value (${start}).`
      if (mid <= min)
        errs.mid = `Mid Value 2 must be greater than Mid Value 1 (${min}).`
      if (max <= mid)
        errs.max = `Maximum Value must be greater than Mid Value 2 (${mid}).`
    }

    return errs
  }

  const handleFieldChange = (field, value) => {
    const updated = { ...selectedItem, [field]: value }
    setselectedItem(updated)
    // Re-run validation live so errors clear as user fixes them
    setErrors(validate(updated))
  }

  const handleSave = async () => {
    const errs = validate(selectedItem)
    if (Object.keys(errs).length) {
      setErrors(errs)
      return
    }

    const APIdata = await FetchPutData(
      setLoading,
      "gauge/ranges",
      selectedItem?.id,
      selectedItem
    )
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Range Values Updated Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
      setEditModal(false)
      setErrors({})
      setTimeout(() => fetchRanges(), 1500)
    } else {
      Swal.fire({
        title: "Error Updating Range Values",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const openEdit = item => {
    setselectedItem(item)
    setErrors({})
    setEditModal(true)
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb
        title="Dashboard"
        breadcrumbItem="Dashboard Gauges - Dynamic Ranges"
      />
      <Card>
        <CardBody>
          <Table striped bordered className="border-dark">
            <thead className="table-dark border-light">
              <tr>
                <td>#</td>
                <td>Gauge Name</td>
                <td>Min Value</td>
                <td>Lower Value (Red Complete)</td>
                <td>Medium Value (Yellow Complete)</td>
                <td>Max Value</td>
                <td>Action</td>
              </tr>
            </thead>
            <tbody>
              {data?.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item?.gauge}</td>
                  <td>{item?.start}</td>
                  <td>{item?.min}</td>
                  <td>{item?.mid}</td>
                  <td>{item?.max}</td>
                  <td>
                    <Button className="btn-blue" onClick={() => openEdit(item)}>
                      <MdEdit />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <Modal isOpen={editModal} size="xl" centered>
        <ModalHeader
          toggle={() => {
            setEditModal(false)
            setErrors({})
          }}
        >
          Edit {selectedItem?.gauge} Range Values
        </ModalHeader>
        <ModalBody>
          <Card className="mb-0">
            <CardBody>
              {/* Order hint */}
              <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
                <i className="mdi mdi-information-outline me-1" />
                Values must be in strict ascending order:&nbsp;
                <strong>
                  Start &lt; Lower Value (Red) &lt; Medium Value (Yellow)&lt;
                  Maximum
                </strong>
              </p>
              <Row>
                <Col md={3}>
                  <Label>Min Value</Label>
                  <Input
                    className="form-control"
                    type="number"
                    invalid={!!errors.start}
                    value={selectedItem?.start ?? ""}
                    onChange={e => handleFieldChange("start", e.target.value)}
                  />
                  {errors.start && <FormFeedback>{errors.start}</FormFeedback>}
                </Col>

                <Col md={3}>
                  <Label>Lower Value (Red Complete)</Label>
                  <Input
                    className="form-control"
                    type="number"
                    invalid={!!errors.min}
                    value={selectedItem?.min ?? ""}
                    onChange={e => handleFieldChange("min", e.target.value)}
                  />
                  {errors.min && <FormFeedback>{errors.min}</FormFeedback>}
                </Col>

                <Col md={3}>
                  <Label>Medium Value (Yellow Complete)</Label>
                  <Input
                    className="form-control"
                    type="number"
                    invalid={!!errors.mid}
                    value={selectedItem?.mid ?? ""}
                    onChange={e => handleFieldChange("mid", e.target.value)}
                  />
                  {errors.mid && <FormFeedback>{errors.mid}</FormFeedback>}
                </Col>

                <Col md={3}>
                  <Label>Maximum Value</Label>
                  <Input
                    className="form-control"
                    type="number"
                    invalid={!!errors.max}
                    value={selectedItem?.max ?? ""}
                    onChange={e => handleFieldChange("max", e.target.value)}
                  />
                  {errors.max && <FormFeedback>{errors.max}</FormFeedback>}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSave}>
            <MdSave /> Save
          </Button>
          <Button
            className="btn-secondary"
            onClick={() => {
              setEditModal(false)
              setErrors({})
            }}
          >
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default RangesIndex
